import React from "react";
import { CookingPot, Bath, BedDouble, Sofa, Trees, Grid2x2, Boxes, LayoutGrid } from "lucide-react";

// Attention taxonomy — muted, premium palette (no neon).
export const ATTENTION = {
  none:      { label: "Not serviced",       dot: "#6b7280", ring: "rgba(107,114,128,0.35)", text: "#9ca3af" },
  standard:  { label: "Standard attention", dot: "#6ee7b7", ring: "rgba(110,231,183,0.30)", text: "#6ee7b7" },
  detailed:  { label: "Detailed attention", dot: "#E7CE93", ring: "rgba(231,206,147,0.35)", text: "#E7CE93" },
  heavy:     { label: "Heavy attention",    dot: "#f4a4a4", ring: "rgba(244,164,164,0.35)", text: "#f4a4a4" },
  specialty: { label: "Specialty / review",  dot: "#cbd5e1", ring: "rgba(203,213,225,0.30)", text: "#cbd5e1" },
};
export const ATTENTION_ORDER = ["standard", "detailed", "heavy", "specialty", "none"];

const ZONE_ICON = {
  kitchen: CookingPot, bathroom: Bath, bedroom: BedDouble, living: Sofa,
  balcony: Trees, windows: Grid2x2, storage: Boxes, other: LayoutGrid,
};
const iconFor = (t) => ZONE_ICON[(t || "other").toLowerCase()] || LayoutGrid;
export const attentionOf = (a) => ATTENTION[a] || ATTENTION.standard;

export const AttentionLegend = () => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="attention-legend">
    {ATTENTION_ORDER.map((k) => (
      <span key={k} className="inline-flex items-center gap-1.5 text-[11px] text-muted-c">
        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: ATTENTION[k].dot }} />
        {ATTENTION[k].label}
      </span>
    ))}
  </div>
);

/**
 * Visual Detail Map — a premium clickable zone grid (not a literal 3D scan).
 * Each zone is colored by its attention level so a client instantly sees
 * "where the money goes".
 */
export const PropertyDetailMap = ({ zones = [], onZoneClick, selectedId }) => {
  if (!zones.length) {
    return (
      <div className="ascendia-card p-8 text-center" data-testid="detail-map-empty">
        <Grid2x2 className="h-7 w-7 text-gold-soft mx-auto" />
        <p className="mt-3 text-white font-medium">No zones mapped yet</p>
        <p className="mt-1 text-sm text-muted-c">Add rooms and zones to build your property's Detail Map.</p>
      </div>
    );
  }
  return (
    <div>
      <div className="mb-4"><AttentionLegend /></div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="detail-map">
        {zones.map((z) => {
          const at = attentionOf(z.attention);
          const Icon = iconFor(z.type);
          const active = selectedId === z.id;
          const dpCount = (z.detail_points || []).length;
          return (
            <button
              key={z.id}
              onClick={() => onZoneClick && onZoneClick(z)}
              data-testid={`zone-tile-${z.id}`}
              className="group relative text-left rounded-xl border bg-white/[0.02] p-4 transition-colors hover:bg-white/[0.05] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--ring)]"
              style={{ borderColor: active ? at.dot : "var(--border-subtle)", boxShadow: active ? `0 0 0 1px ${at.ring}` : "none" }}
            >
              <span className="absolute left-0 top-4 bottom-4 w-1 rounded-full" style={{ backgroundColor: at.dot }} />
              <div className="flex items-start justify-between">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/5 border border-[color:var(--border-subtle)]">
                  <Icon className="h-4.5 w-4.5" style={{ color: at.text }} />
                </span>
                <span className="h-2.5 w-2.5 rounded-full mt-1" style={{ backgroundColor: at.dot }} title={at.label} />
              </div>
              <p className="mt-3 font-medium text-white text-sm leading-tight">{z.name}</p>
              <p className="mt-1 text-[11px]" style={{ color: at.text }}>{at.label}</p>
              {dpCount > 0 && <p className="mt-1 text-[11px] text-muted-c">{dpCount} detail point{dpCount > 1 ? "s" : ""}</p>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PropertyDetailMap;
